import { useRef, useCallback, useEffect, useState } from 'react';
import { render } from 'react-dom';
import PropTypes from 'prop-types';
import { FullScreen } from 'foundations-components/transitional/components/FullScreen';
import { Overlay } from 'foundations-components/transitional/components/Overlay';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { MB_POST_MESSAGE_TYPE, PickerTypes } from '../../constants';
import MessageTypes from '../../constants/MessageTypes';
import { getIframeAppOrigin, logDebug, isMbIframeMessage, getIframeUrl, uniqueId, fireEvent } from '../../utils/iframe';
import MediaBridgeContext from '../internal/MediaBridgeContext';
import PanelIframe from './PanelIframe';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mediaBridgeEl = document.createElement('div');
mediaBridgeEl.className = 'media-bridge-lib-wrapper mce-ignore';
const createIframe = (iframeUrl, pickerType, iframeRef, onRendered) => {
  const existingIframeEl = document.querySelector(`iframe[data-picker-type="${pickerType}"]`);
  if (existingIframeEl) {
    logDebug('Found existing iframe', existingIframeEl);
    iframeRef.current = existingIframeEl;
    onRendered();
    return;
  }
  document.body.appendChild(mediaBridgeEl);
  render( /*#__PURE__*/_jsx(PanelIframe, {
    iframeUrl: iframeUrl,
    ref: iframeRef,
    pickerType: pickerType
  }), mediaBridgeEl, onRendered);
};
let readyQueue = [];
const MediaBridgeProvider = function MediaBridgeProvider({
  children,
  rootNode,
  pickerType = PickerTypes.MEDIA_BRIDGE,
  validatePostMessageSource = true
}) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const iframeRef = useRef(null);
  const uniqueIdRef = useRef(null);
  const onSelectCallback = useRef(null);
  const onCloseCallback = useRef(null);
  const onClickDetailsCallback = useRef(null);
  const postMessageToIframe = useCallback((action, options) => {
    const targetOrigin = getIframeAppOrigin();
    const iframeContentWindow = iframeRef.current ? iframeRef.current.contentWindow : null;
    if (!iframeContentWindow) {
      console.warn('Count not access iframe contentWindow');
      return;
    }
    logDebug('posting message to iframe', action, options);
    iframeContentWindow.postMessage({
      type: MB_POST_MESSAGE_TYPE,
      action,
      options
    }, targetOrigin === 'about:blank' ? '*' : targetOrigin);
  }, []);
  const openPicker = useCallback((options, onSelect, onClose, onClickDetails) => {
    onSelectCallback.current = onSelect;
    onCloseCallback.current = onClose;
    onClickDetailsCallback.current = onClickDetails !== null && onClickDetails !== void 0 ? onClickDetails : null;
    const isDelegatingClickDetails = !!onClickDetails;
    const openIframe = () => {
      setPanelOpen(true);
      iframeRef.current.style.display = 'block';
      postMessageToIframe(MessageTypes.OPEN_PICKER, Object.assign({}, options, {
        isDelegatingClickDetails
      }));
      fireEvent('FileManagerLib:Drawer:open');
    };
    if (iframeLoaded) {
      openIframe();
      iframeRef.current.style.display = 'block';
    } else {
      logDebug('Placing openPicker in ready queue for provider', uniqueIdRef.current);
      readyQueue.push(openIframe);
      if (iframeRef.current) {
        const iframeUrl = getIframeUrl(pickerType);
        // todo - perhaps eliminate by requiring `openPicker` be called on mount vs waiting for interaction
        logDebug('Setting existing iframe src to ', iframeUrl);
        iframeRef.current.src = iframeUrl;
      }
    }
  }, [pickerType, postMessageToIframe, iframeLoaded]);
  const onMessageReceivedFromIframe = useCallback(e => {
    if (isMbIframeMessage(e)) {
      if (validatePostMessageSource && e.source !== iframeRef.current.contentWindow) {
        logDebug('message source is not expected iframe', e);
        return;
      }
      logDebug('received message from iframe', e.data);
      const {
        action
      } = e.data;
      switch (action) {
        case MessageTypes.IFRAME_READY:
          iframeRef.current.setAttribute('data-loaded', 'true');
          setIframeLoaded(true);
          if (readyQueue.length) {
            readyQueue.map(onReady => onReady());
            readyQueue = [];
          }
          break;
        case MessageTypes.CLOSE_PANEL:
          setPanelOpen(false);
          if (onCloseCallback.current) {
            onCloseCallback.current();
          }
          break;
        case MessageTypes.SELECT_FILE:
        case MessageTypes.INSERT_MEDIA:
          setPanelOpen(false);
          if (onSelectCallback.current) {
            onSelectCallback.current(e.data.payload);
          }
          break;
        case MessageTypes.CLICK_DETAILS:
          {
            const {
              video
            } = e.data.payload;
            if (onClickDetailsCallback.current) {
              setPanelOpen(false);
              onClickDetailsCallback.current(video);
            }
            break;
          }
        default:
          break;
      }
    }
  }, [validatePostMessageSource]);
  useEffect(() => {
    logDebug('panelOpen change', panelOpen, iframeRef.current);
    if (!panelOpen) {
      fireEvent('FileManagerLib:Drawer:close');
    }
    if (iframeRef.current) {
      iframeRef.current.style.display = panelOpen ? 'block' : 'none';
    }
  }, [panelOpen]);
  useEffect(() => {
    uniqueIdRef.current = uniqueId();
    logDebug('Mount provider id', uniqueIdRef.current);
    createIframe('about:blank', pickerType, iframeRef, () => {
      if (iframeRef.current && iframeRef.current.getAttribute('data-loaded')) {
        logDebug('Iframe already loaded for provider', uniqueIdRef.current, iframeRef.current);
        setIframeLoaded(true);
        if (readyQueue.length) {
          logDebug('Executing readyQueue for provider', uniqueIdRef.current);
          readyQueue.map(onReady => onReady());
          readyQueue = [];
        }
      } else if (readyQueue.length && iframeRef.current && iframeRef.current.src === 'about:blank') {
        const iframeUrl = getIframeUrl(pickerType);
        logDebug(`Updating non-eager iframe src to ${iframeUrl}`);
        iframeRef.current.src = iframeUrl;
      }
    });
    window.addEventListener('message', onMessageReceivedFromIframe);
    return () => {
      window.removeEventListener('message', onMessageReceivedFromIframe);
      logDebug(`Provider id ${uniqueIdRef.current} unmounted`);
    };
  }, [pickerType, onMessageReceivedFromIframe]);
  const renderPanelOverlay = () => {
    return iframeLoaded ? /*#__PURE__*/_jsx(Overlay, {
      "data-test-id": "mb-overlay",
      width: "100%"
    }) : /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "provider-loading-overlay"
    });
  };
  const unmountPicker = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.style.display = 'none';
    }
    setPanelOpen(false);
  }, []);
  return /*#__PURE__*/_jsxs(MediaBridgeContext.Provider, {
    value: {
      unmountPicker,
      iframeLoaded,
      panelOpen,
      openPicker
    },
    children: [/*#__PURE__*/_jsx(FullScreen, {
      rootNode: rootNode,
      "data-layer-for": "media-bridge-provider",
      children: renderPanelOverlay()
    }), children]
  });
};
MediaBridgeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  rootNode: PropTypes.instanceOf(Element),
  pickerType: PropTypes.oneOf(Object.values(PickerTypes)).isRequired,
  validatePostMessageSource: PropTypes.bool
};
export default MediaBridgeProvider;